import TextareaInput, { type TextareaInputProps } from '@creator/ui/components/Form/TextareaInput/TextareaInput';
import Spinner from '@creator/ui/components/Spinner/Spinner';
import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import { cn } from '@creator/ui/utils/ui';
import { extractUrls } from '@creator/ui/utils/url-utils';
import LinkMetaInputWrapper from '@src/basic-components/CommentInput/LinkMetaInputWrapper';
import Icon from '@src/basic-components/Icon/Icon';
import JoinButtonModal from '@src/components/JoinButtonModal/JoinButtonModal';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { ensurePostUrl } from '@src/model/upvote/helpers';
import { getDisplayName, getUserId, isUserJoinedToToken } from '@src/model/user/helpers';
import { createAnonymousComment, createComment } from '@src/store/comments/create';
import type { Comment } from '@src/store/contentEntities/model';
import type { LinkMeta } from '@src/types/link-meta';
import { isValidUrl } from '@src/utils/url-utils/url-utils';
import { noop } from '@src/utils/utils';
import type { FC } from 'react';
import { Fragment, memo, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import LinkMetaFetcher, { type LinkMetaFetcherProps } from './LinkMetaFetcher/LinkMetaFetcher';

const MAX_CHARS_IN_COMMENT = 2000;

export interface CommentTextareaProps {
    placeholder?: string;
    postId: string;
    replyToCommentId?: string;
    tokenName: string;
    hideProfileImage?: boolean;
    inputClassName?: string;
    className?: string;
}

const QuickCommentInput: FC<CommentTextareaProps> = props => {
    const { replyToCommentId = '', postId, tokenName, className, placeholder, hideProfileImage = false } = props;

    const { t } = useTranslation();
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const [value, setValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [linkMetaUrl, setLinkMetaUrl] = useState('');
    const [linkMeta, setLinkMeta] = useState<LinkMeta>();
    const navigate = useNavigate();
    const { handleModal } = useContext<UseModal>(ModalContext);

    const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
    }, []);

    const handleLinkMetaUrlChange = useCallback((url: string) => {
        setLinkMetaUrl(url);
    }, []);

    const handleLinkMetaSuccess = useCallback((meta: LinkMeta) => {
        setLinkMeta(meta);
    }, []);

    const handleLinkMetaDelete = useCallback(() => {
        setLinkMeta(undefined);
        setLinkMetaUrl('');
    }, []);

    function mapError(error) {
        if (error.message.includes('banned')) return alert(t('errors.bannedFromCommunity'));
        if (error.message.includes('is hidden by domain admin')) return alert(t('unableToUndelete'));
        if (error.message.includes('rate limit')) return alert(t('rateLimitError'));
        if (error.message.includes('is hidden')) return alert(error.message);
        // if (error.message.includes('is hidden')) return alert(t('cantCommentPostHidden'));
        alert(`${t('generalErrorMessage')} ${error.message}`);
    }

    async function onSubmit() {
        if (isLoggedIn && !isUserJoinedToToken(tokenName)) return handleModal(<JoinButtonModal tokenName={tokenName} />);
        if (!value) return;
        setIsSending(true);

        const publisherId = getUserId();

        try {
            let comment: Comment;
            if (publisherId) {
                comment = await createComment({
                    postId,
                    replyToCommentId,
                    tokenName,
                    publisherId: getUserId(),
                    content: value,
                    taggedUsers: [],
                    linkMeta: linkMeta || null,
                });
            } else {
                comment = await createAnonymousComment({
                    postId,
                    replyToCommentId,
                    tokenName,
                    content: value,
                });
            }

            setValue('');
            setLinkMeta(undefined);
            setLinkMetaUrl('');
            setIsSending(false);
            if (replyToCommentId) return;
            navigate(`/${ensureTokenUrl(tokenName)}/${ensurePostUrl(postId)}/comment/${comment.id}`, {
                state: { preventScrollToTop: true },
            });
        } catch (error) {
            mapError(error);
            setIsSending(false);
            console.error(error);
        }
    }

    function getInputClassName() {
        const { inputClassName } = props;

        let base = `flex-1 text-sm rounded-lg px-2 transition group cursor-pointer flex items-center 
        border border-gray-200 dark:border-gray-600 hover:border-gray-500 dark:hover:border-gray-500 
        focus-within:border-gray-500 dark:focus-within:border-gray-500 `;
        if (value) base = `${base} border-gray-500 dark:border-gray-500`;

        return cn(base, inputClassName);
    }

    function getClassName() {
        const base = 'flex items-center';
        return cn(base, className);
    }

    function renderProfileImage() {
        if (hideProfileImage) return null;
        return <ProfileImage className="flex-shrink-0 w-6 h-6 mr-2" displayName={getDisplayName() || 'anonymous'} tokenName={tokenName} />;
    }

    const isImageUrl = useCallback((url: string): boolean => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico'];
        const urlLower = url.toLowerCase();
        return imageExtensions.some(ext => urlLower.includes(ext));
    }, []);

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            const pastedText = e.clipboardData.getData('text');
            const urls = extractUrls(pastedText);

            if (!urls.length) return;
            const url = urls[0];
            if (!url || !isValidUrl(url)) return;

            // Check if it's an image URL
            if (isImageUrl(url)) {
                e.preventDefault();
                const textarea = e.currentTarget;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const markdownImage = `![Image](${url})`;

                // Insert markdown image syntax at cursor position
                const newValue = value.substring(0, start) + markdownImage + value.substring(end);
                setValue(newValue);

                // Set cursor position after the inserted markdown
                setTimeout(() => {
                    textarea.selectionStart = textarea.selectionEnd = start + markdownImage.length;
                }, 0);
            } else {
                // For non-image URLs, set link meta
                setLinkMetaUrl(url);
            }
        },
        [isImageUrl, value]
    );

    function renderCommentButton() {
        const buttonClassName = value ? 'flex p-1 text-sm cursor-pointer text-gray-900 dark:text-gray-50' : 'flex p-1 text-sm cursor-pointer text-gray-400 dark:text-gray-600';

        return (
            <button type="button" onClick={onSubmit} className={buttonClassName}>
                {isSending && <Spinner className="w-4 h-4" />}
                {!isSending && <Icon className="transition" name="sendTransparent" />}
            </button>
        );
    }

    function renderTextarea() {
        const _props: TextareaInputProps = {
            value,
            onChange,
            onPaste: handlePaste,
            placeholder: placeholder || t('quickCommentInput.placeholder'),
            maxLength: MAX_CHARS_IN_COMMENT,
            className: 'pr-2 flex-1 text-base resize-none bg-transparent dark:bg-transparent px-0 border-none py-0',
            enableAutosize: true,
            rows: 1,
        };

        return <TextareaInput {..._props} />;
    }

    function renderLinkMetaFetcher() {
        const _props: LinkMetaFetcherProps = {
            initialLinkMeta: linkMeta,
            onSuccess: handleLinkMetaSuccess,
            onError: noop,
            onDelete: handleLinkMetaDelete,
            className: 'mt-2',
            url: linkMetaUrl,
        };

        return <LinkMetaFetcher {..._props} />;
    }

    return (
        <Fragment>
            <div className={getClassName()}>
                {renderProfileImage()}
                <div className={cn('', getInputClassName())}>
                    {renderTextarea()}
                    {renderCommentButton()}
                </div>
            </div>
            {renderLinkMetaFetcher()}
        </Fragment>
    );
};

export default memo(QuickCommentInput);
